import * as React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Pencil,
  Trash2,
  Plus,
  Eye,
} from 'lucide-react';
import { api, Client, ApiError } from '@/lib/api';
import AddPlatformModal from './AddPlatformModal';

export default function ClientDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const id = params.id as string;

  const [client, setClient] = React.useState<Client | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isDeletingTenant, setIsDeletingTenant] = React.useState<string | null>(null);
  const [showAddPlatformModal, setShowAddPlatformModal] = React.useState(false);

  const loadClient = React.useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.getClient(id);
      setClient(data);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Erro ao carregar cliente');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  React.useEffect(() => {
    loadClient();
  }, [loadClient]);

  const handleDelete = async () => {
    if (!confirm('Tem certeza que deseja excluir este cliente? Esta acao nao pode ser desfeita e todas as plataformas associadas serao removidas.')) {
      return;
    }

    try {
      setIsDeleting(true);
      await api.deleteClient(id);
      navigate('/clients');
    } catch (err) {
      const apiError = err as ApiError;
      alert(apiError.message || 'Erro ao excluir cliente');
      setIsDeleting(false);
    }
  };

  const handleDeleteTenant = async (tenantSlug: string) => {
    if (!confirm('Tem certeza que deseja excluir esta plataforma? Esta acao nao pode ser desfeita.')) {
      return;
    }

    try {
      setIsDeletingTenant(tenantSlug);
      await api.deleteTenant(tenantSlug);
      // Reload client to update tenants list
      const data = await api.getClient(id);
      setClient(data);
    } catch (err) {
      const apiError = err as ApiError;
      alert(apiError.message || 'Erro ao excluir plataforma');
    } finally {
      setIsDeletingTenant(null);
    }
  };


  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-[#0057FF]" />
          <p className="text-gray-500">Carregando cliente...</p>
        </div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link
            to="/clients"
            className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Cliente nao encontrado</h1>
        </div>
        <div className="bg-red-50 rounded-2xl border border-red-100 p-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-red-500" />
            </div>
            <div>
              <h3 className="font-semibold text-red-800">Erro</h3>
              <p className="text-red-600 text-sm">{error || 'Cliente nao encontrado'}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/clients"
            className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#0057FF] to-[#7C3AED] flex items-center justify-center shadow-lg shadow-[#0057FF]/20">
              <span className="text-white font-bold text-xl">
                {client.name.charAt(0)}
              </span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{client.name}</h1>
              <div className="flex items-center gap-3 mt-1">
                <div className="flex items-center gap-1.5">
                  <div className={`h-2 w-2 rounded-full ${client.status === 'active' ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                  <span className="text-sm text-gray-500">
                    {client.status === 'active' ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to={`/clients/${id}/edit`}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <Pencil className="h-4 w-4" />
            Editar
          </Link>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-50 border border-red-100 text-sm font-medium text-red-600 hover:bg-red-100 transition-colors disabled:opacity-50"
          >
            {isDeleting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            Excluir
          </button>
        </div>
      </div>

      {/* Client Info */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Basic Info */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Informacoes Gerais</h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-500">ID</span>
              <code className="text-sm font-mono text-gray-900 bg-gray-50 px-2 py-1 rounded">{client.id}</code>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-500">Nome</span>
              <span className="text-sm font-medium text-gray-900">{client.name}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-500">Status</span>
              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                client.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'
              }`}>
                {client.status === 'active' ? 'Ativo' : 'Inativo'}
              </span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-500">Criado em</span>
              <span className="text-sm text-gray-900">{new Date(client.createdAt).toLocaleDateString('pt-BR')}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-500">Atualizado em</span>
              <span className="text-sm text-gray-900">{new Date(client.updatedAt).toLocaleDateString('pt-BR')}</span>
            </div>
          </div>
        </div>

        {/* Plataformas */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Plataformas</h2>
              <span className="text-sm text-gray-500">
                {client.tenants?.length || 0} {client.tenants?.length === 1 ? 'plataforma' : 'plataformas'}
              </span>
            </div>
            <button
              onClick={() => setShowAddPlatformModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0057FF] text-white text-sm font-medium hover:bg-[#0046CC] transition-colors"
            >
              <Plus className="h-4 w-4" />
              Adicionar Plataforma
            </button>
          </div>
          <div className="p-6">
            {client.tenants && client.tenants.length > 0 ? (
              <div className="space-y-3">
                {client.tenants.map((tenant) => (
                  <div key={tenant.id} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{tenant.platformName}</p>
                      <code className="text-xs text-gray-500">{tenant.slug}.nozcomunidades.com.br</code>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        tenant.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {tenant.status === 'active' ? 'Ativo' : 'Inativo'}
                      </span>
                      <div className="flex items-center gap-1">
                        <Link
                          to={`/tenants/${tenant.slug}`}
                          className="p-2 rounded-lg text-gray-400 hover:text-[#0057FF] hover:bg-[#0057FF]/10 transition-colors"
                          title="Visualizar plataforma"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                        <Link
                          to={`/tenants/${tenant.slug}/edit`}
                          className="p-2 rounded-lg text-gray-400 hover:text-[#0057FF] hover:bg-[#0057FF]/10 transition-colors"
                          title="Editar plataforma"
                        >
                          <Pencil className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => handleDeleteTenant(tenant.slug)}
                          disabled={isDeletingTenant === tenant.slug}
                          className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                          title="Excluir plataforma"
                        >
                          {isDeletingTenant === tenant.slug ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 text-sm">Nenhuma plataforma cadastrada</p>
                <p className="text-gray-400 text-xs mt-1">Clique em "Adicionar Plataforma" para criar a primeira</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Platform Modal */}
      {showAddPlatformModal && (
        <AddPlatformModal
          clientId={id}
          onClose={() => setShowAddPlatformModal(false)}
          onSuccess={() => {
            setShowAddPlatformModal(false);
            loadClient();
          }}
        />
      )}
    </div>
  );
}
